import { useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { analytics } from '@/lib/analytics';
import { captureBusinessEvent } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';

interface LogContext {
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
  [key: string]: any; // Allow additional properties
}

export const useLogger = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const logInfo = useCallback((message: string, context: LogContext = {}) => {
    const logData = {
      level: 'info',
      message,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      ...context
    };

    console.info('📋 INFO:', logData);
    
    // Send to Sentry as breadcrumb
    captureBusinessEvent(message, logData, 'info');
  }, [user]);

  const logWarning = useCallback((message: string, context: LogContext = {}) => {
    const logData = {
      level: 'warning',
      message,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      ...context
    };

    console.warn('⚠️ WARNING:', logData);
    
    // Send to Sentry
    captureBusinessEvent(message, logData, 'warning');
  }, [user]);

  const logError = useCallback((error: Error | string, context: LogContext = {}) => {
    const message = typeof error === 'string' ? error : error.message;
    const logData = {
      level: 'error',
      message,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      stack: typeof error === 'object' ? error.stack : undefined,
      ...context
    };

    console.error('❌ ERROR:', logData);
    
    // Send to analytics
    analytics.errorOccurred(message, logData, user?.id);
    
    // Send to Sentry
    captureBusinessEvent(message, logData, 'error');
    
    // Show user-friendly toast
    toast({
      title: "Κάτι πήγε στραβά",
      description: "Προσπαθήστε ξανά. Αν το πρόβλημα συνεχίζεται, επικοινωνήστε με την υποστήριξη.",
      variant: "destructive",
    });
  }, [user, toast]);

  const logBusinessEvent = useCallback((event: string, data: Record<string, any> = {}) => {
    const logData = {
      event,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      ...data
    };

    console.log('🎯 BUSINESS EVENT:', logData);
    
    // Track in analytics
    analytics.featureUsed(event, user?.id || 'anonymous', data);
    
    // Send to Sentry as breadcrumb
    captureBusinessEvent(event, logData, 'info');
  }, [user]);

  const logNetworkError = useCallback((url: string, status: number, error: string) => {
    const logData = {
      type: 'network_error',
      url,
      status,
      error,
      userId: user?.id,
      timestamp: new Date().toISOString()
    };

    console.error('🌐 NETWORK ERROR:', logData);
    
    // Send to analytics
    analytics.errorOccurred(`Network error: ${status} ${url}`, logData, user?.id);
    
    // Show appropriate toast based on error type
    if (status >= 500) {
      toast({
        title: "Πρόβλημα διακομιστή",
        description: "Προσπαθήστε ξανά σε λίγα λεπτά.",
        variant: "destructive",
      });
    } else if (status === 401) {
      toast({
        title: "Μη εξουσιοδοτημένη πρόσβαση",
        description: "Παρακαλώ συνδεθείτε ξανά.",
        variant: "destructive",
      });
    } else if (status >= 400) {
      toast({
        title: "Σφάλμα αιτήματος",
        description: "Ελέγξτε τα δεδομένα και προσπαθήστε ξανά.",
        variant: "destructive",
      });
    }
  }, [user, toast]);

  return {
    logInfo,
    logWarning,
    logError,
    logBusinessEvent,
    logNetworkError
  };
};